import React, { useState, useEffect } from "react";
import {
    Modal,
    Form,
} from "react-bootstrap";
import {
    TextField,
    Box,
    Autocomplete,
    Button,
    Grid,
    Typography,
    FormControlLabel,
    Switch,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import Spinner from "../../sections/Spinner";

const securityTypes = [
    "permanent",
    "temporary",
    "unarmed",
    "armed",
    "patrol",
];

const periodValues = [
    { value: "none", label: "None" },
    { value: "everyday", label: "Everyday" },
    { value: "once_a_week", label: "Once a Week" },
    { value: "twice_a_week", label: "Twice a Week" },
    { value: "once_a_month", label: "Once a Month" },
    { value: "twice_a_month", label: "Twice a Month" },
];

const ContractorPopup = ({
    open,
    onClose,
    initialData,
    onSubmit,
    loading,
    readOnly = false,
}) => {
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        phoneNo: "",
        contractorAddress: "",
        city: "",
        state: "",
        country: "",
        zipcode: "",
        typeOfSecurity: "",
        noOfGuardRequired: "",
        startDate: "",
        endDate: "",
        startTime: "",
        endTime: "",
        message: "",
        reminderStatus: false,
        reminderTime: null,
        reminderDuration: "",
        reminderComment: "",
    });

    useEffect(() => {
        if (initialData) {
            setFormData({
                name: initialData.name || "",
                email: initialData.email || "",
                phoneNo: initialData.phoneNo || "",
                contractorAddress: initialData.contractorAddress || "",
                city: initialData.city || "",
                state: initialData.state || "",
                country: initialData.country || "",
                zipcode: initialData.zipcode || "",
                typeOfSecurity: initialData.typeOfSecurity || "",
                noOfGuardRequired: initialData.noOfGuardRequired || "",
                startDate: initialData.startDate ? dayjs(initialData.startDate).format("YYYY-MM-DD") : "",
                endDate: initialData.endDate ? dayjs(initialData.endDate).format("YYYY-MM-DD") : "",
                startTime: initialData.startTime ? dayjs(initialData.startTime).format("HH:mm") : "",
                endTime: initialData.endTime ? dayjs(initialData.endTime).format("HH:mm") : "",
                message: initialData.message || "",
                reminderStatus: initialData.reminderStatus || false,
                reminderTime: initialData.reminderTime ? dayjs(initialData.reminderTime) : null,
                reminderDuration: initialData.reminderDuration || "",
                reminderComment: initialData.reminderComment || "",
            });
        } else {
            setFormData({
                name: "",
                email: "",
                phoneNo: "",
                contractorAddress: "",
                city: "",
                state: "",
                country: "",
                zipcode: "",
                typeOfSecurity: "",
                noOfGuardRequired: "",
                startDate: "",
                endDate: "",
                startTime: "",
                endTime: "",
                message: "",
                reminderStatus: false,
                reminderTime: null,
                reminderDuration: "",
                reminderComment: "",
            });
        }
    }, [initialData, open]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleAutocompleteChange = (name, value) => {
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleFormSubmit = (e) => {
        e.preventDefault();
        const dataToSubmit = {
            ...formData,
            reminderTime: formData.reminderTime ? formData.reminderTime.toISOString() : "",
            // API expects start/end time as date strings usually, but let's send what matches securityBasePanel
            startTime: formData.startTime ? dayjs(`2000-01-01 ${formData.startTime}`).toISOString() : "",
            endTime: formData.endTime ? dayjs(`2000-01-01 ${formData.endTime}`).toISOString() : "",
        };
        onSubmit(dataToSubmit);
    };

    return (
        <Modal show={open} onHide={onClose} size="lg" centered scrollable>
            <Modal.Header closeButton>
                <Modal.Title>
                    {readOnly ? "View Contractor" : initialData ? "Edit Contractor" : "Add New Contractor"}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form onSubmit={handleFormSubmit}>
                    <Typography variant="h6" sx={{ mb: 2 }}>General Information</Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Name"
                                name="name"
                                size="small"
                                required
                                value={formData.name}
                                onChange={handleChange}
                                disabled={readOnly}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Email"
                                name="email"
                                type="email"
                                size="small"
                                required
                                value={formData.email}
                                onChange={handleChange}
                                disabled={readOnly}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Phone No."
                                name="phoneNo"
                                size="small"
                                value={formData.phoneNo}
                                onChange={handleChange}
                                disabled={readOnly}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Autocomplete
                                options={securityTypes}
                                value={formData.typeOfSecurity}
                                onChange={(e, newValue) => handleAutocompleteChange("typeOfSecurity", newValue)}
                                disabled={readOnly}
                                renderInput={(params) => (
                                    <TextField {...params} label="Type of Security" size="small" required />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="No. of Guards Required"
                                name="noOfGuardRequired"
                                type="number"
                                size="small"
                                value={formData.noOfGuardRequired}
                                onChange={handleChange}
                                disabled={readOnly}
                            />
                        </Grid>
                    </Grid>

                    <Typography variant="h6" sx={{ mb: 2, mt: 3 }}>Address Details</Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Address"
                                name="contractorAddress"
                                size="small"
                                value={formData.contractorAddress}
                                onChange={handleChange}
                                disabled={readOnly}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="City"
                                name="city"
                                size="small"
                                value={formData.city}
                                onChange={handleChange}
                                disabled={readOnly}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="State"
                                name="state"
                                size="small"
                                value={formData.state}
                                onChange={handleChange}
                                disabled={readOnly}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Country"
                                name="country"
                                size="small"
                                value={formData.country}
                                onChange={handleChange}
                                disabled={readOnly}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Zipcode"
                                name="zipcode"
                                size="small"
                                value={formData.zipcode}
                                onChange={handleChange}
                                disabled={readOnly}
                            />
                        </Grid>
                    </Grid>

                    <Typography variant="h6" sx={{ mb: 2, mt: 3 }}>Contract Schedule</Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Start Date"
                                name="startDate"
                                type="date"
                                size="small"
                                InputLabelProps={{ shrink: true }}
                                value={formData.startDate}
                                onChange={handleChange}
                                disabled={readOnly}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="End Date"
                                name="endDate"
                                type="date"
                                size="small"
                                InputLabelProps={{ shrink: true }}
                                value={formData.endDate}
                                onChange={handleChange}
                                disabled={readOnly}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Start Time"
                                name="startTime"
                                type="time"
                                size="small"
                                InputLabelProps={{ shrink: true }}
                                value={formData.startTime}
                                onChange={handleChange}
                                disabled={readOnly}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="End Time"
                                name="endTime"
                                type="time"
                                size="small"
                                InputLabelProps={{ shrink: true }}
                                value={formData.endTime}
                                onChange={handleChange}
                                disabled={readOnly}
                            />
                        </Grid>
                    </Grid>

                    <TextField
                        fullWidth
                        label="Message"
                        name="message"
                        multiline
                        rows={2}
                        sx={{ mt: 3 }}
                        value={formData.message}
                        onChange={handleChange}
                        disabled={readOnly}
                    />

                    <hr className="my-4" />
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" sx={{ flexGrow: 1 }}>Reminder Settings</Typography>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.reminderStatus}
                                    onChange={(e) => handleAutocompleteChange("reminderStatus", e.target.checked)}
                                    name="reminderStatus"
                                    color="primary"
                                    disabled={readOnly}
                                />
                            }
                            label={formData.reminderStatus ? "Enabled" : "Disabled"}
                        />
                    </Box>

                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DateTimePicker
                                    label="Reminder Time"
                                    value={formData.reminderTime}
                                    onChange={(newValue) => handleAutocompleteChange("reminderTime", newValue)}
                                    disabled={readOnly || !formData.reminderStatus}
                                    sx={{ width: '100%' }}
                                    slotProps={{ textField: { size: 'small' } }}
                                />
                            </LocalizationProvider>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Autocomplete
                                options={periodValues}
                                getOptionLabel={(option) => option.label || (periodValues.find(p => p.value === option)?.label || option)}
                                value={periodValues.find(p => p.value === formData.reminderDuration) || null}
                                onChange={(e, newValue) => handleAutocompleteChange("reminderDuration", newValue?.value || "")}
                                disabled={readOnly || !formData.reminderStatus}
                                renderInput={(params) => (
                                    <TextField {...params} label="Frequency" size="small" />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Reminder Comment"
                                name="reminderComment"
                                multiline
                                rows={2}
                                value={formData.reminderComment}
                                onChange={handleChange}
                                disabled={readOnly || !formData.reminderStatus}
                            />
                        </Grid>
                    </Grid>

                    {!readOnly && (
                        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                            <Button variant="outlined" onClick={onClose}>Cancel</Button>
                            <Button variant="contained" type="submit" disabled={loading}>
                                {loading ? <Spinner /> : initialData ? "Update" : "Submit"}
                            </Button>
                        </Box>
                    )}
                </Form>
            </Modal.Body>
        </Modal>
    );
};

export default ContractorPopup;
